import { supabase } from './supabase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'Authorization': `Bearer ${session?.access_token}`,
    'Content-Type': 'application/json',
  };
}

async function getAuthHeadersMultipart() {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'Authorization': `Bearer ${session?.access_token}`,
  };
}

export async function fetchApi(path, options = {}) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}${path}`, { headers, ...options });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

// Ideas API
export const ideasApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return fetchApi(`/api/ideas${qs ? `?${qs}` : ''}`);
  },

  get: (id) => fetchApi(`/api/ideas/${id}`),

  create: (idea) => fetchApi('/api/ideas', {
    method: 'POST',
    body: JSON.stringify(idea),
  }),

  update: (id, updates) => fetchApi(`/api/ideas/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),

  delete: (id) => fetchApi(`/api/ideas/${id}`, {
    method: 'DELETE',
  }),

  uploadAsset: async (ideaId, file) => {
    const headers = await getAuthHeadersMultipart();
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(`${API_URL}/api/ideas/${ideaId}/assets`, {
      method: 'POST',
      headers,
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Upload failed');
    return data;
  },

  deleteAsset: (ideaId, assetId) => fetchApi(`/api/ideas/${ideaId}/assets/${assetId}`, {
    method: 'DELETE',
  }),

  profile: (id) => fetchApi(`/api/ideas/${id}/profile`, {
    method: 'POST',
  }),
};

// Companies API
export const companiesApi = {
  list: () => fetchApi('/api/companies'),
  get: (id) => fetchApi(`/api/companies/${id}`),
  create: (name) => fetchApi('/api/companies', { method: 'POST', body: JSON.stringify({ name }) }),
};

// Company Assets API
export const companyAssetsApi = {
  list: (companyId) => fetchApi(`/api/companies/${companyId}/assets`),

  upload: async (companyId, file, assetType = 'other') => {
    const headers = await getAuthHeadersMultipart();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('asset_type', assetType);

    const res = await fetch(`${API_URL}/api/companies/${companyId}/assets`, {
      method: 'POST',
      headers,
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Upload failed');
    return data;
  },

  delete: (companyId, assetId) => fetchApi(`/api/companies/${companyId}/assets/${assetId}`, {
    method: 'DELETE',
  }),
};

// Matching API
export const matchingApi = {
  run: (ideaId) => fetchApi(`/api/match/${ideaId}`, { method: 'POST' }),
  getCached: (ideaId) => fetchApi(`/api/match/${ideaId}`),
};

// Trends API
export const trendsApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return fetchApi(`/api/trends${qs ? `?${qs}` : ''}`);
  },
  getMatches: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return fetchApi(`/api/trend-matches${qs ? `?${qs}` : ''}`);
  },
  updateMatchStatus: (id, status) => fetchApi(`/api/trend-matches/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  }),
  scan: () => fetchApi('/api/trends/scan', { method: 'POST' }),
  unreadCount: () => fetchApi('/api/trends/unread-count'),
};

// Events API
export const eventsApi = {
  list: () => fetchApi('/api/events'),
  get: (id) => fetchApi(`/api/events/${id}`),
  create: (data) => fetchApi('/api/events', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => fetchApi(`/api/events/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => fetchApi(`/api/events/${id}`, { method: 'DELETE' }),

  // Companies
  addCompanyFull: (eventId, data) => fetchApi(`/api/events/${eventId}/companies/full`, { method: 'POST', body: JSON.stringify(data) }),
  removeCompany: (eventId, companyId) => fetchApi(`/api/events/${eventId}/companies/${companyId}`, { method: 'DELETE' }),
  uploadNdaFile: async (eventId, companyId, file) => {
    const headers = await getAuthHeadersMultipart();
    const formData = new FormData();
    formData.append('nda_file', file);
    const res = await fetch(`${API_URL}/api/events/${eventId}/companies/${companyId}/nda`, { method: 'POST', headers, body: formData });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  },

  // Submissions
  getSubmissions: (eventId) => fetchApi(`/api/events/${eventId}/submissions`),
  updateSubmission: (eventId, subId, data) => fetchApi(`/api/events/${eventId}/submissions/${subId}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Public submission (no auth)
  submitPublic: async (eventId, formData) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/submissions`, { method: 'POST', body: formData });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  },

  // Stripe Checkout for sponsorship
  checkoutSponsorship: async (eventId, data) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/checkout/sponsorship`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error);
    return json;
  },

  // Stripe Checkout for inventor application fee
  checkoutInventorFee: async (eventId, data) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/checkout/inventor-fee`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error);
    return json;
  },

  // Get sponsors for event (public)
  getSponsors: async (eventId) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/sponsors`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error);
    return json.sponsors || [];
  },

  // Matching & Assignments
  matchSubmissions: (eventId) => fetchApi(`/api/events/${eventId}/match-submissions`, { method: 'POST' }),
  getAssignments: (eventId) => fetchApi(`/api/events/${eventId}/assignments`),
  updateAssignment: (eventId, assignId, status) => fetchApi(`/api/events/${eventId}/assignments/${assignId}`, { method: 'PUT', body: JSON.stringify({ status }) }),
  publish: (eventId) => fetchApi(`/api/events/${eventId}/publish`, { method: 'POST' }),

  // Bulk assignment actions (D-05)
  bulkUpdateAssignments: (eventId, ids, status, rejectionNote) =>
    fetchApi(`/api/events/${eventId}/assignments/bulk`, {
      method: 'PUT',
      body: JSON.stringify({ ids, status, rejection_note: rejectionNote })
    }),

  // Manual assignment (D-09)
  createManualAssignment: (eventId, inventorId, companyId, ideaId) =>
    fetchApi(`/api/events/${eventId}/assignments/manual`, {
      method: 'POST',
      body: JSON.stringify({ inventor_id: inventorId, company_id: companyId, idea_id: ideaId })
    }),

  // Organizer dashboard (D-25)
  getOrganizerDashboard: (eventId) =>
    fetchApi(`/api/events/${eventId}/organizer-dashboard`),

  // NDA retry (D-02)
  retryNda: (eventId, ndaId) =>
    fetchApi(`/api/events/${eventId}/ndas/${ndaId}/retry`, { method: 'POST' }),

  // NDAs
  getNdas: (eventId) => fetchApi(`/api/events/${eventId}/ndas`),
  updateNda: (eventId, ndaId, data) => fetchApi(`/api/events/${eventId}/ndas/${ndaId}`, { method: 'PUT', body: JSON.stringify(data) }),
  uploadSignedNda: async (eventId, ndaId, file) => {
    const formData = new FormData();
    formData.append('signed_nda', file);
    const res = await fetch(`${API_URL}/api/events/${eventId}/ndas/${ndaId}/upload`, { method: 'POST', body: formData });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  },

  // Inventor view
  myAssignments: (eventId) => fetchApi(`/api/events/${eventId}/my-assignments`),

  // Slots
  createSlot: (eventId, data) => fetchApi(`/api/events/${eventId}/slots`, { method: 'POST', body: JSON.stringify(data) }),
  updateSlot: (eventId, slotId, data) => fetchApi(`/api/events/${eventId}/slots/${slotId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSlot: (eventId, slotId) => fetchApi(`/api/events/${eventId}/slots/${slotId}`, { method: 'DELETE' }),
  // Slot cancellation (D-20) — reopens slot with 48-hour cutoff enforcement
  cancelSlot: (eventId, slotId) =>
    fetchApi(`/api/events/${eventId}/slots/${slotId}`, { method: 'DELETE' }),
};

// IR Pipeline API
export const irApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return fetchApi(`/api/ir/submissions${qs ? `?${qs}` : ''}`);
  },
  get: (id) => fetchApi(`/api/ir/submissions/${id}`),
  create: (data) => fetchApi('/api/ir/submissions', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, updates) => fetchApi(`/api/ir/submissions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  delete: (id) => fetchApi(`/api/ir/submissions/${id}`, { method: 'DELETE' }),
  draftResponse: (id) => fetchApi(`/api/ir/submissions/${id}/draft-response`, { method: 'POST' }),
  stats: () => fetchApi('/api/ir/stats'),
};

// Pitch API
export const pitchApi = {
  generate: (data) => fetchApi('/api/pitch/generate', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return fetchApi(`/api/pitch/scripts${qs ? `?${qs}` : ''}`);
  },
  get: (id) => fetchApi(`/api/pitch/scripts/${id}`),
  update: (id, content) => fetchApi(`/api/pitch/scripts/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ content }),
  }),
  delete: (id) => fetchApi(`/api/pitch/scripts/${id}`, { method: 'DELETE' }),
};

// BOM API
export const bomApi = {
  generate: (ideaId, inputText) => fetchApi('/api/bom/generate', {
    method: 'POST',
    body: JSON.stringify({ idea_id: ideaId, input_text: inputText }),
  }),
  listForIdea: (ideaId) => fetchApi(`/api/bom/${ideaId}`),
  update: (id, updates) => fetchApi(`/api/bom/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  delete: (id) => fetchApi(`/api/bom/${id}`, { method: 'DELETE' }),
};

// Meeting Notes API
export const meetingNotesApi = {
  list: () => fetchApi('/api/meeting-notes'),
  create: (data) => fetchApi('/api/meeting-notes', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  delete: (id) => fetchApi(`/api/meeting-notes/${id}`, { method: 'DELETE' }),
  runAnalysis: () => fetchApi('/api/notes/analyze', { method: 'POST' }),
  getStatus: () => fetchApi('/api/notes/analyze/status'),
  getInsights: () => fetchApi('/api/notes/insights'),
  markRead: (insightId) => fetchApi(`/api/notes/insights/${insightId}/read`, { method: 'PATCH' }),
};
